import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import { getPredictionMarketsDiscover } from '../services/market.service.api';
import { MarketQueryKeys, STALE_TIME_5_MIN } from '../constants/query';
import {
  syncDiscoveryData,
  syncDiscoveryError,
  syncDiscoveryLoading,
} from '../state/discovery.sync';
import type {
  PredictionMarketDiscoverResponse,
  GetPredictionMarketsDiscoverParams,
} from '../services/interfaces/market.types';

type UsePredictionMarketsDiscoverOptions = {
  params?: GetPredictionMarketsDiscoverParams;
  enabled?: boolean;
  staleTime?: number;
  syncToStore?: boolean; // Option to disable auto-sync if needed
  queryOptions?: Omit<
    UseQueryOptions<PredictionMarketDiscoverResponse, Error>,
    'queryKey' | 'queryFn' | 'enabled' | 'staleTime'
  >;
};

export const usePredictionMarketsDiscover = ({
  params,
  enabled = true,
  staleTime = STALE_TIME_5_MIN,
  syncToStore = true,
  queryOptions,
}: UsePredictionMarketsDiscoverOptions = {}) => {
  const { onSuccess, onError } = useMemo(() => {
    if (!syncToStore) {
      return {
        onSuccess: queryOptions?.onSuccess,
        onError: queryOptions?.onError,
      };
    }

    return {
      onSuccess: (data: PredictionMarketDiscoverResponse) => {
        syncDiscoveryData(data);
        queryOptions?.onSuccess?.(data);
      },
      onError: (error: Error) => {
        syncDiscoveryError(error);
        queryOptions?.onError?.(error);
      },
    };
  }, [syncToStore, queryOptions]);

  const query = useQuery<PredictionMarketDiscoverResponse, Error>({
    queryKey: MarketQueryKeys.PREDICTION_MARKETS_DISCOVER(params),
    queryFn: () => getPredictionMarketsDiscover(params),
    enabled,
    staleTime,
    onSuccess,
    onError,
    ...queryOptions,
  });

  useEffect(() => {
    if (syncToStore) {
      syncDiscoveryLoading(query.isFetching);
    }
  }, [query.isFetching, syncToStore]);

  return query;
};
